import React, { useState, useEffect } from 'react';
import { getCollection, updateDocument, deleteDocument } from '../services/resilientDb';
import './ApplicationsPage.css';

const ApplicationsPage = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('pending');
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        fetchApplications();
    }, []);

    const fetchApplications = async () => {
        try {
            const appsList = await getCollection('jobApplications');
            setApplications(appsList);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching applications:', error);
            setLoading(false);
        }
    };

    const handleApprove = async (appId) => {
        const app = applications.find(a => a.id === appId);
        if (!app) return;

        if (window.confirm('Approve this application? The worker will be marked as verified and active.')) {
            try {
                // 1. Mark application as approved
                await updateDocument('jobApplications', appId, {
                    status: 'approved',
                    reviewedAt: new Date().toISOString()
                });

                // 2. Update the worker's serviceProviders doc with service details
                if (app.userId && app.userId !== 'anonymous') {
                    await updateDocument('serviceProviders', app.userId, {
                        isVerified: true,
                        isActive: true,
                        serviceCategory: app.serviceCategory || '',
                        service: app.serviceCategory || '',
                        experience: app.experience || '',
                        hourlyRate: app.hourlyRate || '',
                        skills: app.skills || '',
                        certifications: app.certifications || '',
                        availability: app.availability || {},
                        bio: app.bio || '',
                        city: app.city || '',
                        state: app.state || '',
                        address: app.address || '',
                        zipCode: app.zipCode || '',
                        verifiedAt: new Date().toISOString()
                    });
                }

                setApplications(applications.map(a =>
                    a.id === appId ? { ...a, status: 'approved', reviewedAt: new Date().toISOString() } : a
                ));
                alert('Application approved! Worker is now verified and active.');
            } catch (error) {
                console.error('Error approving application:', error);
                alert('Failed to approve application');
            }
        }
    };

    const handleReject = async (appId) => {
        if (window.confirm('Reject this application?')) {
            try {
                await updateDocument('jobApplications', appId, {
                    status: 'rejected',
                    reviewedAt: new Date().toISOString()
                });
                setApplications(applications.map(app =>
                    app.id === appId ? { ...app, status: 'rejected', reviewedAt: new Date().toISOString() } : app
                ));
                alert('Application rejected.');
            } catch (error) {
                console.error('Error rejecting application:', error);
                alert('Failed to reject application');
            }
        }
    };

    const handleDelete = async (appId) => {
        if (window.confirm('Permanently delete this application?')) {
            try {
                await deleteDocument('jobApplications', appId);
                setApplications(applications.filter(app => app.id !== appId));
                alert('Application deleted.');
            } catch (error) {
                console.error('Error deleting application:', error);
                alert('Failed to delete application');
            }
        }
    };

    const filteredApplications = applications.filter(app => {
        const matchesSearch =
            app.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            app.serviceCategory?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            app.city?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterStatus === 'all' || app.status === filterStatus;
        return matchesSearch && matchesFilter;
    });

    const pendingCount = applications.filter(a => a.status === 'pending').length;
    const approvedCount = applications.filter(a => a.status === 'approved').length;
    const rejectedCount = applications.filter(a => a.status === 'rejected').length;

    const getStatusClass = (status) => {
        switch (status) {
            case 'approved': return 'status-approved';
            case 'rejected': return 'status-rejected';
            default: return 'status-pending';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            const date = typeof dateStr === 'object' && dateStr.toDate
                ? dateStr.toDate()
                : new Date(dateStr);
            return date.toLocaleDateString('en-IN', {
                day: 'numeric', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
        } catch {
            return 'N/A';
        }
    };

    if (loading) {
        return (
            <div className="applications-page">
                <div className="container">
                    <div className="loading">Loading applications...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="applications-page">
            <div className="container">
                <div className="page-header">
                    <div>
                        <h1>Provider Applications</h1>
                        <p>Review and manage service provider applications</p>
                    </div>
                    <div className="stats-row">
                        <div className="stat-badge pending-badge">
                            <span className="stat-count">{pendingCount}</span>
                            <span>Pending</span>
                        </div>
                        <div className="stat-badge approved-badge">
                            <span className="stat-count">{approvedCount}</span>
                            <span>Approved</span>
                        </div>
                        <div className="stat-badge rejected-badge">
                            <span className="stat-count">{rejectedCount}</span>
                            <span>Rejected</span>
                        </div>
                    </div>
                </div>

                <div className="filters-section">
                    <div className="search-box">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Search by name, email, service, city..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="service-filter"
                    >
                        <option value="all">All ({applications.length})</option>
                        <option value="pending">Pending ({pendingCount})</option>
                        <option value="approved">Approved ({approvedCount})</option>
                        <option value="rejected">Rejected ({rejectedCount})</option>
                    </select>
                </div>

                {filteredApplications.length === 0 ? (
                    <div className="empty-state">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <polyline points="10 9 9 9 8 9" />
                        </svg>
                        <h3>No applications found</h3>
                        <p>No applications match your current filters.</p>
                    </div>
                ) : (
                    <div className="applications-list">
                        {filteredApplications.map((app) => (
                            <div key={app.id} className={`application-card ${expandedId === app.id ? 'expanded' : ''}`}>
                                <div className="app-card-header" onClick={() => setExpandedId(expandedId === app.id ? null : app.id)}>
                                    <div className="app-card-avatar">
                                        {app.fullName?.charAt(0).toUpperCase() || '?'}
                                    </div>
                                    <div className="app-card-summary">
                                        <h3>{app.fullName || 'Unknown Applicant'}</h3>
                                        <div className="app-card-meta">
                                            <span className="app-service">{app.serviceCategory || 'No service'}</span>
                                            <span className="app-city">{app.city || 'No city'}</span>
                                            <span className="app-date">{formatDate(app.appliedAt)}</span>
                                        </div>
                                    </div>
                                    <div className={`app-status ${getStatusClass(app.status)}`}>
                                        {app.status || 'pending'}
                                    </div>
                                    <svg className={`expand-icon ${expandedId === app.id ? 'rotated' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <polyline points="6 9 12 15 18 9" />
                                    </svg>
                                </div>

                                {expandedId === app.id && (
                                    <div className="app-card-details">
                                        <div className="details-grid">
                                            <div className="detail-group">
                                                <h4>Contact Information</h4>
                                                <div className="detail-item">
                                                    <span className="detail-label">Email</span>
                                                    <span className="detail-value">{app.email || 'N/A'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Phone</span>
                                                    <span className="detail-value">{app.phone || 'N/A'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Address</span>
                                                    <span className="detail-value">
                                                        {[app.address, app.city, app.state, app.zipCode].filter(Boolean).join(', ') || 'N/A'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="detail-group">
                                                <h4>Professional Details</h4>
                                                <div className="detail-item">
                                                    <span className="detail-label">Service</span>
                                                    <span className="detail-value">{app.serviceCategory || 'N/A'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Experience</span>
                                                    <span className="detail-value">{app.experience ? `${app.experience} years` : 'N/A'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Hourly Rate</span>
                                                    <span className="detail-value">{app.hourlyRate ? `₹${app.hourlyRate}` : 'N/A'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Skills</span>
                                                    <span className="detail-value">{app.skills || 'N/A'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Certifications</span>
                                                    <span className="detail-value">{app.certifications || 'N/A'}</span>
                                                </div>
                                            </div>

                                            <div className="detail-group full-width">
                                                <h4>Availability</h4>
                                                <div className="availability-tags">
                                                    {app.availability?.weekdays && <span className="avail-tag">Weekdays</span>}
                                                    {app.availability?.weekends && <span className="avail-tag">Weekends</span>}
                                                    {app.availability?.emergency && <span className="avail-tag emergency">Emergency</span>}
                                                    {!app.availability?.weekdays && !app.availability?.weekends && !app.availability?.emergency && (
                                                        <span className="avail-tag none">Not specified</span>
                                                    )}
                                                </div>
                                            </div>

                                            {app.bio && (
                                                <div className="detail-group full-width">
                                                    <h4>Professional Bio</h4>
                                                    <p className="bio-text">{app.bio}</p>
                                                </div>
                                            )}

                                            {app.whyJoin && (
                                                <div className="detail-group full-width">
                                                    <h4>Why They Want to Join</h4>
                                                    <p className="bio-text">{app.whyJoin}</p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="app-card-actions">
                                            {app.status === 'pending' && (
                                                <>
                                                    <button onClick={() => handleApprove(app.id)} className="approve-btn">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                            <polyline points="20 6 9 17 4 12" />
                                                        </svg>
                                                        Approve
                                                    </button>
                                                    <button onClick={() => handleReject(app.id)} className="reject-btn">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                            <line x1="18" y1="6" x2="6" y2="18" />
                                                            <line x1="6" y1="6" x2="18" y2="18" />
                                                        </svg>
                                                        Reject
                                                    </button>
                                                </>
                                            )}
                                            <button onClick={() => handleDelete(app.id)} className="delete-btn">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                    <polyline points="3 6 5 6 21 6" />
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                </svg>
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ApplicationsPage;
